import { AlertTriangle } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { EMPTY, formatDate, humanize, monthsUntil } from "./format"

// Badge tones, one map per vocabulary. Every lookup falls through to a neutral
// outline so a value the backend grows later renders as itself rather than
// blank — these lists are operator-editable (MasterValue), so "unknown" is a
// normal state, not a bug.
const NEUTRAL = "border-slate-400/40 text-slate-600 dark:text-slate-400"

/**
 * When the next requalification falls due. Red once it's past, orange inside
 * six months — the window where planning it is still cheaper than expediting
 * it.
 */
export function QualDueBadge({ dueOn }: { dueOn: string | null | undefined }) {
  if (!dueOn) {
    return <span className="text-muted-foreground text-sm">{EMPTY}</span>
  }

  const months = monthsUntil(dueOn)
  const overdue = months != null && months < 0
  const soon = months != null && months >= 0 && months <= 6

  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs whitespace-nowrap tabular-nums",
        overdue
          ? "border-red-500/50 text-red-600 dark:text-red-400"
          : soon
            ? "border-amber-500/50 text-amber-600 dark:text-amber-400"
            : NEUTRAL,
      )}
    >
      {formatDate(dueOn)}
    </Badge>
  )
}

/**
 * The money flag: this car's lease ends before its next qual is due, so
 * testing it buys compliance the lessee will never use. Return the car
 * instead.
 */
export function RequalConflictBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="border-red-500/50 text-xs whitespace-nowrap text-red-600 dark:text-red-400"
        >
          <AlertTriangle className="mr-1 size-3" aria-hidden="true" />
          Conflict
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        Lease expires before requal — review before testing.
      </TooltipContent>
    </Tooltip>
  )
}

/** A lease rider's computed state, plus an expiry warning inside six months. */
export function RiderStatusBadge({
  status,
  expiringSoon,
}: {
  status: string
  expiringSoon?: boolean
}) {
  const tone =
    status === "expired"
      ? "border-red-500/50 text-red-600 dark:text-red-400"
      : expiringSoon
        ? "border-amber-500/50 text-amber-600 dark:text-amber-400"
        : status === "active"
          ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
          : NEUTRAL

  return (
    <Badge variant="outline" className={cn("text-xs whitespace-nowrap", tone)}>
      {humanize(status)}
    </Badge>
  )
}

/** Active / inactive. A retired car keeps its history — it is never deleted. */
export function CarStatusBadge({ status }: { status: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs",
        status === "active"
          ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
          : NEUTRAL,
      )}
    >
      {humanize(status)}
    </Badge>
  )
}

const WAITING_TONE: Record<string, string> = {
  lessor: "border-amber-500/50 text-amber-600 dark:text-amber-400",
  fra: "border-purple-500/50 text-purple-600 dark:text-purple-400",
  shop_transit: "border-blue-500/50 text-blue-600 dark:text-blue-400",
  shop: "border-blue-500/50 text-blue-600 dark:text-blue-400",
  mru: "border-teal-500/50 text-teal-600 dark:text-teal-400",
  internal: NEUTRAL,
}

const WAITING_LABEL: Record<string, string> = {
  lessor: "Lessor",
  fra: "FRA",
  shop_transit: "Shop (in transit)",
  shop: "Shop",
  mru: "MRU",
  internal: "Us",
}

/** Who the clock is running against. The question the product exists to answer. */
export function WaitingOnBadge({
  waitingOn,
}: {
  waitingOn: string | null | undefined
}) {
  if (!waitingOn) {
    return <span className="text-muted-foreground text-sm">{EMPTY}</span>
  }

  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs whitespace-nowrap",
        WAITING_TONE[waitingOn] ?? NEUTRAL,
      )}
    >
      {WAITING_LABEL[waitingOn] ?? humanize(waitingOn)}
    </Badge>
  )
}

const WORK_ORDER_TYPE_LABEL: Record<string, string> = {
  BadOrder: "Bad order",
  Requalification: "Requal",
  LeaseReturn: "Lease return",
  Cleaning: "Cleaning",
  Restencil: "Restencil",
}

export function WorkOrderTypeChip({ type }: { type: string }) {
  return (
    <Badge variant="secondary" className="text-xs whitespace-nowrap">
      {WORK_ORDER_TYPE_LABEL[type] ?? humanize(type)}
    </Badge>
  )
}

/** Work-order status. Closed reads green; everything open reads neutral. */
export function WorkOrderStatusBadge({
  status,
  closed,
}: {
  status: string
  closed?: boolean
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs whitespace-nowrap",
        closed
          ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
          : NEUTRAL,
      )}
    >
      {humanize(status)}
    </Badge>
  )
}

/** Document verification: Ruby's verdict over the LLM's extraction. */
export function VerificationBadge({
  result,
}: {
  result: Record<string, unknown> | null | undefined
}) {
  const status = result?.status
  if (status !== "passed" && status !== "failed") return null

  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs",
        status === "passed"
          ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
          : "border-red-500/50 text-red-600 dark:text-red-400",
      )}
    >
      {status === "passed" ? "Verified" : "Failed verification"}
    </Badge>
  )
}
